import React, { useState } from "react";
import "./resultsTable.css";
import PageHeader from "../layouts/pageHeader/pageHeader";
import Pagination from "./pagination";
import Operations from "./operations";
import NoResults from "./noResults";
import CrfDocLink from "../layouts/crfDocLink/crfDocLink";
import CdeDetailsLink from "../layouts/cdeDetailsLink/cdeDetailsLink";

export default function ResultsTable({ data, reportType }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState([]);

  const resetSelectedRows = () => {
    setSelectedRows([]);
  };

  const handleCheckboxChange = (id) => {
    if (id === "all") {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.length === data.length
          ? []
          : data.map((item) => ({
              id: reportType === "CRF" ? item.crfId : item.cdeId + item.crfName,
              ...(reportType === "CRF" && { externalUrl: item.externalUrl }),
            }))
      );
    } else {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.some((row) => row.id === id)
          ? prevSelectedRows.filter((row) => row.id !== id)
          : [
              ...prevSelectedRows,
              {
                id,
                ...(reportType === "CRF" && {
                  externalUrl: data.find((item) =>
                    reportType === "CRF"
                      ? item.crfId === id
                      : item.cdeId + item.crfName === id
                  ).externalUrl,
                }),
              },
            ]
      );
    }
  };

  //console.log(selectedRows);

  let operations;
  let columns;
  let pageHeaderDesc;
  if (reportType === "CRF") {
    pageHeaderDesc =
      "Download results by clicking the checkboxes on the far right side of the table. Select the header checkbox to select all CRFs listed on the first page. Then choose 'Download Table Contents into Zip File' from the Choose an operation dropdown menu.";
    operations = [
      "- Choose an operation -",
      "Download Table Contents into Zip file",
    ];
    columns = [
      "CRF Module/Guideline",
      "Description",
      "CDEs",
      "Version #",
      "Version Date",
      "Disease Name",
      "Sub Disease Name",
      "Copyright",
    ];
  } else {
    pageHeaderDesc =
      "The search results below can be downloaded by clicking the checkboxes on the far right side of the table. Select the header checkbox to select all CDEs listed on the first page. Then choose CDE Detailed Report from the Choose an operation dropdown menu.";
    operations = ["- Choose an operation -", "CDE Detailed Report"];
    columns = [
      "CDE ID",
      "CDE Name",
      "Definition",
      "Classification",
      "Version Number",
      "Version Date",
      "CRF Name",
      "©, ® or TM",
      "Disease Name",
      "Sub Disease Name",
    ];
  }

  const totalPages = Math.ceil(data.length / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const paginatedData = data.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const tableData = () => {
    if (reportType === "CRF") {
      return paginatedData.map((r) => (
        <tr key={r.crfId}>
          <td>
            <input
              type="checkbox"
              checked={selectedRows.some((row) => row.id === r.crfId)}
              onChange={() => handleCheckboxChange(r.crfId)}
            />
          </td>
          <td>
            <CrfDocLink 
              crfExtension={r.crfExtension}
              crfId={r.crfId}
              crfName={r.crfName}
              externalUrl={r.externalUrl}
            />
          </td>
          <td>
            {r.crfDescription ? r.crfDescription : "No description available"}
          </td>
          <td>
            <CdeDetailsLink 
              associatedCDE={r.associatedCDE}
              crfId={r.crfId}
              crfName={r.crfName}
              domainName={r.domainName}
              reportType={reportType}
              subDomainName={r.subDomainName}
            />
          </td>
          <td>{r.version}</td>
          <td>{r.versionDate}</td>
          <td>{r.diseaseName}</td>
          <td>{r.subDiseaseName}</td>
          <td>{r.copyright ? "True" : "False"}</td>
        </tr>
      ));
    } else {
      return paginatedData.map((r) => (
        <tr key={r.cdeId}>
          <td>
            <input
              type="checkbox"
              checked={selectedRows.some(
                (row) => row.id === r.cdeId + r.crfName
              )}
              onChange={() => handleCheckboxChange(r.cdeId + r.crfName)}
            />
          </td>
          <td>{r.cdeId}</td>
          <td>{r.cdeName}</td>
          <td>{r.definition}</td>
          <td>{r.classification}</td>
          <td>{r.version}</td>
          <td>{r.versionDate}</td>
          <td>{r.crfName}</td>
          <td>{r.copyright ? "True" : "False"}</td>
          <td>{r.diseaseName}</td>
          <td>{r.subDiseaseName}</td>
        </tr>
      ));
    }
  };

  return (
    <>
      {data.length > 0 && (
        <div className="results-table">
          <Operations
            operations={operations}
            selectedRows={selectedRows}
            reportType={reportType}
            resetSelectedRows={resetSelectedRows}
          />
          <PageHeader>{pageHeaderDesc}</PageHeader>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          <table>
            <thead>
              <tr>
                <th
                  className="selectAllHeader"
                  onClick={() => handleCheckboxChange("all")}
                >
                  Select All
                </th>
                {columns.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>{tableData()}</tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
      {data.length === 0 && <NoResults />}
    </>
  );
}
